/**
 * The button. Prototype: docs/HRMS.jsx line 826-838.
 *
 * Tones carry meaning (business-behaviour doc §2): primary = the main action,
 * ghost = secondary, yellow (dark) = emphatic secondary, danger = destructive.
 * Destructive actions are never violet.
 *
 * The prototype faked "disabled" by setting `opacity: .5` and a not-allowed cursor
 * while leaving the handler live; here `disabled` is the real attribute and the CSS
 * reproduces the same look, so a keyboard user cannot fire a blocked action.
 */
import type { LucideIcon } from "lucide-react";
import type { CSSProperties, MouseEvent, ReactNode } from "react";
import "./ui.css";

export type BtnTone = "primary" | "ghost" | "yellow" | "danger";
export type BtnSize = "sm" | "md";

export interface BtnProps {
  children?: ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  tone?: BtnTone;
  size?: BtnSize;
  icon?: LucideIcon;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  title?: string;
  /** Required when the button has an icon but no text. */
  "aria-label"?: string;
  "aria-expanded"?: boolean;
  "aria-controls"?: string;
  className?: string;
  style?: CSSProperties;
}

export function Btn({
  children,
  onClick,
  tone = "primary",
  size = "md",
  icon: Icon,
  disabled = false,
  type = "button",
  title,
  className,
  style,
  ...aria
}: BtnProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={["ui-btn", `ui-btn--${size}`, `ui-btn--${tone}`, className].filter(Boolean).join(" ")}
      style={style}
      {...aria}
    >
      {Icon ? <Icon size={size === "sm" ? 13 : 14} aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export default Btn;
